import { getUniqueAddItemKey, getUniqueOrderLineKey } from '../../utils/orderLineUtils';
import { convertAddItemToOrderLine, getTotalItemPrice, getTotalItemTakeawayPrice } from './ClientCartMapper';
import { AddItemDto, AddItemsDto, CartOrderLine, CartOrderLineComponent, ClientCartDto } from '../../../types/cart';

export const findOrderLine = (item: AddItemDto, cart: ClientCartDto): CartOrderLine | undefined => {
  return cart.orderLines.find((ol) => getUniqueOrderLineKey(ol) === getUniqueAddItemKey(item));
};

export const updateOrderLine = (orderLine: CartOrderLine, orderLines: CartOrderLine[]): CartOrderLine[] => {
  const index = orderLines.findIndex((ol) => getUniqueOrderLineKey(ol) === getUniqueOrderLineKey(orderLine));

  if (index === -1) return orderLines;

  const updatedOrderLine = {
    ...orderLine,
    totalPrice: getTotalItemPrice(orderLine),
    totalTakeawayPrice: getTotalItemTakeawayPrice(orderLine),
  };

  return [...orderLines.slice(0, index), updatedOrderLine, ...orderLines.slice(index + 1)];
};

export const handleDelete = (prevCart: ClientCartDto, deleteItems: AddItemsDto): ClientCartDto => {
  const existingOrderLine = findOrderLine(deleteItems.item, prevCart);

  if (!existingOrderLine) {
    return prevCart;
  }

  const quantityAfterDecrease = existingOrderLine.quantity - deleteItems.quantity;

  if (quantityAfterDecrease <= 0) {
    // If quantity zero or below, remove by uniqueOrderLineKey
    const orderLinesAfterDelete = prevCart.orderLines.filter(
      (ol) => getUniqueOrderLineKey(ol) !== getUniqueOrderLineKey(existingOrderLine)
    );

    return {
      ...prevCart,
      orderLines: orderLinesAfterDelete,
    };
  }

  const updatedOrderLine = {
    ...existingOrderLine,
    quantity: quantityAfterDecrease,
  };

  const orderLinesAfterUpdate = updateOrderLine(updatedOrderLine, prevCart.orderLines);

  return { ...prevCart, orderLines: orderLinesAfterUpdate };
};

export const handleAddItem = (prevCart: ClientCartDto, addItem: AddItemDto, quantity = 1): ClientCartDto => {
  // Items with comments should always create new line items (quantity = 1)
  const hasComment = addItem.comment && addItem.comment.length > 0;

  if (!hasComment) {
    const existingOrderLine = findOrderLine(addItem, prevCart);

    if (existingOrderLine) {
      const updatedOrderLine = {
        ...existingOrderLine,
        quantity: existingOrderLine.quantity + quantity,
      };

      const orderLinesAfterUpdate = updateOrderLine(updatedOrderLine, prevCart.orderLines);

      return { ...prevCart, orderLines: orderLinesAfterUpdate };
    }
  }

  // Generate commentId if adding a new item with a comment
  if (hasComment && !addItem.commentId) {
    addItem.commentId = crypto.randomUUID();
  }

  const orderLine = convertAddItemToOrderLine(addItem, quantity);
  const allOrderLines = [...prevCart.orderLines, orderLine];

  return {
    ...prevCart,
    orderLines: allOrderLines,
  };
};

export const handleSetOrderLineComment = (
  orderLineKey: string,
  prevCart: ClientCartDto,
  comment?: string
): ClientCartDto => {
  const existingIndex = prevCart.orderLines.findIndex((ol) => getUniqueOrderLineKey(ol) === orderLineKey);

  if (existingIndex === -1) {
    return prevCart;
  }

  const existingOrderLine = prevCart.orderLines[existingIndex]!;

  // Generate commentId when comment is first added, remove it when comment is cleared
  let commentId = existingOrderLine.commentId;
  if (comment && comment.length > 0 && !commentId) {
    // First time adding a comment - generate UUID
    commentId = crypto.randomUUID();
  }
  if (!comment || comment.length === 0) {
    // Comment is being cleared - remove commentId
    commentId = undefined;
  }

  const updatedOrderLine: CartOrderLine = {
    ...existingOrderLine,
    comment: comment,
    commentId: commentId,
  };

  // Recalculate prices
  updatedOrderLine.totalPrice = getTotalItemPrice(updatedOrderLine);
  updatedOrderLine.totalTakeawayPrice = getTotalItemTakeawayPrice(updatedOrderLine);

  // Replace at the same index, since the key has changed (commentId was added/removed)
  return {
    ...prevCart,
    orderLines: [
      ...prevCart.orderLines.slice(0, existingIndex),
      updatedOrderLine,
      ...prevCart.orderLines.slice(existingIndex + 1),
    ],
  };
};

export interface CompositeUpdateData {
  orderLineKey: string;
  parentOptions: AddItemDto['options'];
  parentComment?: string;
  parentCourseId?: string;
  quantity: number;
  componentUpdates: Array<{
    itemVariantId: string;
    options: AddItemDto['options'];
    comment?: string;
    courseId?: string;
  }>;
  components?: CartOrderLineComponent[];
}

/**
 * Recursively updates component options and comments.
 */
const updateComponents = (
  components: CartOrderLineComponent[],
  updates: CompositeUpdateData['componentUpdates']
): CartOrderLineComponent[] => {
  return components.map((component) => {
    const update = updates.find((u) => u.itemVariantId === component.itemVariantId);

    // Handle commentId: generate if new comment, preserve if existing, remove if cleared
    let componentCommentId = component.commentId;
    if (update?.comment && update.comment.length > 0) {
      if (!componentCommentId) {
        componentCommentId = crypto.randomUUID();
      }
    }
    if (!update?.comment || update.comment.length === 0) {
      componentCommentId = undefined;
    }

    // Recursively update nested components
    const nestedComponents = component.components ? updateComponents(component.components, updates) : undefined;

    return {
      ...component,
      options: update?.options ?? component.options,
      comment: update?.comment,
      commentId: componentCommentId,
      courseId: update?.courseId ?? component.courseId,
      components: nestedComponents,
    };
  });
};

export interface CompositeSplitGroup {
  quantity: number;
  componentUpdates: CompositeUpdateData['componentUpdates'];
}

export interface CompositeSplitData {
  orderLineKey: string;
  parentOptions: CompositeUpdateData['parentOptions'];
  parentComment?: string;
  parentCourseId?: string;
  groups: CompositeSplitGroup[];
}

export const handleSplitCompositeProduct = (prevCart: ClientCartDto, splitData: CompositeSplitData): ClientCartDto => {
  const parentIndex = prevCart.orderLines.findIndex((ol) => getUniqueOrderLineKey(ol) === splitData.orderLineKey);

  if (parentIndex === -1) {
    return prevCart;
  }

  const parentOrderLine = prevCart.orderLines[parentIndex]!;

  const newLines: CartOrderLine[] = splitData.groups.map((group) => {
    const updatedComponents = parentOrderLine.components
      ? updateComponents(parentOrderLine.components, group.componentUpdates)
      : undefined;

    let parentCommentId = parentOrderLine.commentId;
    if (splitData.parentComment && splitData.parentComment.length > 0) {
      if (!parentCommentId) {
        parentCommentId = crypto.randomUUID();
      }
    }
    if (!splitData.parentComment || splitData.parentComment.length === 0) {
      parentCommentId = undefined;
    }

    const line: CartOrderLine = {
      ...parentOrderLine,
      quantity: group.quantity,
      options: splitData.parentOptions,
      comment: splitData.parentComment,
      commentId: parentCommentId,
      courseId: splitData.parentCourseId ?? parentOrderLine.courseId,
      components: updatedComponents,
    };

    line.totalPrice = getTotalItemPrice(line);
    line.totalTakeawayPrice = getTotalItemTakeawayPrice(line);

    return line;
  });

  return {
    ...prevCart,
    orderLines: [
      ...prevCart.orderLines.slice(0, parentIndex),
      ...newLines,
      ...prevCart.orderLines.slice(parentIndex + 1),
    ],
  };
};

export const handleUpdateCompositeProduct = (prevCart: ClientCartDto, update: CompositeUpdateData): ClientCartDto => {
  // Find parent order line by key
  const parentIndex = prevCart.orderLines.findIndex((ol) => getUniqueOrderLineKey(ol) === update.orderLineKey);

  if (parentIndex === -1) {
    return prevCart;
  }

  const parentOrderLine = prevCart.orderLines[parentIndex]!;

  // Handle parent commentId: generate if new comment, preserve if existing, remove if cleared
  let parentCommentId = parentOrderLine.commentId;
  if (update.parentComment && update.parentComment.length > 0) {
    if (!parentCommentId) {
      parentCommentId = crypto.randomUUID();
    }
  }
  if (!update.parentComment || update.parentComment.length === 0) {
    parentCommentId = undefined;
  }

  // Use full replacement if provided, otherwise update recursively by itemVariantId
  const updatedComponents =
    update.components ??
    (parentOrderLine.components ? updateComponents(parentOrderLine.components, update.componentUpdates) : undefined);

  const updatedParent: CartOrderLine = {
    ...parentOrderLine,
    quantity: update.quantity,
    options: update.parentOptions,
    comment: update.parentComment,
    commentId: parentCommentId,
    courseId: update.parentCourseId ?? parentOrderLine.courseId,
    components: updatedComponents,
  };

  // Recalculate prices
  updatedParent.totalPrice = getTotalItemPrice(updatedParent);
  updatedParent.totalTakeawayPrice = getTotalItemTakeawayPrice(updatedParent);

  return {
    ...prevCart,
    orderLines: [
      ...prevCart.orderLines.slice(0, parentIndex),
      updatedParent,
      ...prevCart.orderLines.slice(parentIndex + 1),
    ],
  };
};
